/**
 * Confinement rules for scoped (limited) portal admins — the recognition-only
 * admin (backend role recognition_admin, JWT claim scp: ['recognition']) and the
 * read-only recognition viewer (recognition_viewer, scp: ['recognition_viewer']).
 * Mirrors the onboarding-client gate in Router.js: the per-route canAccessPortal
 * guard admits them, and the global beforeEach gate (plus the App.vue cold-load
 * watcher) redirects anything outside the allowed set back to /recognition. Pure
 * module (no State import) so it stays unit-testable — same pattern as
 * utils/mfaGate.js.
 */

export const RECOGNITION_SCOPE = 'recognition';
export const RECOGNITION_VIEWER_SCOPE = 'recognition_viewer';

// Auth/recovery flows and profile. 'profile' must stay reachable: the MFA-setup
// gate redirects there. 'not-found' must stay: route-guard failures land there,
// and confining it would loop when the company's recognition_version is 'off'.
const SHARED_ALLOWED_ROUTES = [
  'login',
  'login-impersonate',
  'forgot-password',
  'reset-password',
  'profile',
  'not-found',
];

// A viewer changes nothing, so the authoring and funding pages are unreachable
// rather than merely inert.
export const RECOGNITION_VIEWER_ALLOWED_ROUTES = new Set([
  'recognition',
  'recognition-award-detail',
  ...SHARED_ALLOWED_ROUTES,
]);

export const SCOPED_ADMIN_ALLOWED_ROUTES = new Set([
  ...RECOGNITION_VIEWER_ALLOWED_ROUTES,
  'recognition-create-program',
  'recognition-create-open',
  'recognition-create-milestone',
  'recognition-settings',
  'recognition-duplicate-source',
  'recognition-group-editor',
]);

const hasOnlyScope = (claims, scope) => {
  if (!claims || claims.a || claims.m || claims.is || claims.oc) {
    return false;
  }
  return Array.isArray(claims.scp) && claims.scp.includes(scope);
};

export const isRecognitionOnlyClaims = claims => hasOnlyScope(claims, RECOGNITION_SCOPE);

// Someone holding both roles carries both scopes; the wider one wins, so
// read-only mode never hides pages they are entitled to use.
export const isRecognitionViewerClaims = claims =>
  hasOnlyScope(claims, RECOGNITION_VIEWER_SCOPE) && !isRecognitionOnlyClaims(claims);

export const isScopedRecognitionClaims = claims => isRecognitionOnlyClaims(claims) || isRecognitionViewerClaims(claims);

export const shouldConfineScopedAdmin = ({ claims, routeName }) => {
  if (isRecognitionOnlyClaims(claims)) {
    return !SCOPED_ADMIN_ALLOWED_ROUTES.has(routeName);
  }
  if (isRecognitionViewerClaims(claims)) {
    return !RECOGNITION_VIEWER_ALLOWED_ROUTES.has(routeName);
  }
  return false;
};
